/**
 * Task Repository
 *
 * Handles all task-related data access. Part of the v2 refactor (PR #3 per
 * docs/v2/REFACTOR_BLUEPRINT.md §3.11). Extracted from services/api.ts (the
 * corresponding api.ts functions become thin wrappers that delegate here).
 *
 * Key v2 seam: createTask accepts optional alert_id to wire the Alert→Task
 * bridge (the FK column added by migration 20260422000006).
 *
 * @module services/repositories/TaskRepository
 */

import { supabase } from '../supabase';
import type { Task, TaskStatus } from '../../types';

// ============================================
// STATUS + PRIORITY MAPPING
// ============================================

/**
 * tasks.status vocabulary. Since migration 20260711100000 (applied to cloud)
 * tasks_status_check allows exactly these values — the UI's TaskStatus — so
 * statuses are stored as-is. The legacy pending/completed/cancelled values were
 * migrated away and are rejected by the constraint.
 */
export const TASK_STATUSES = ['todo', 'in_progress', 'pending_review', 'approved', 'rejected', 'done'] as const satisfies readonly TaskStatus[];

/** Status to store for a task write; a task created without one starts in 'todo'. */
export function toDbTaskStatus(status: TaskStatus | undefined): TaskStatus {
  return status ?? 'todo';
}

const DB_VALID_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

/**
 * Update a task with core fields plus optional review-metadata columns. The
 * metadata columns (review_info, submitted_for_review_*) come from migration
 * 20260124000001, which is not deployed everywhere; PostgREST rejects the whole
 * payload with PGRST204 when it names an unknown column, so retry with the core
 * fields — the status transition must persist either way.
 */
async function updateTaskWithOptionalColumns(
  id: string,
  tenantId: string,
  core: Record<string, unknown>,
  optional: Record<string, unknown>,
): Promise<{ error: { code?: string } | null }> {
  const first = await supabase.from('tasks').update({ ...core, ...optional }).eq('id', id).eq('tenant_id', tenantId);
  if (first.error?.code !== 'PGRST204') return first;
  return supabase.from('tasks').update(core).eq('id', id).eq('tenant_id', tenantId);
}

/** SECURITY: every task write is scoped by tenant_id as well as id. */
function requireTenant(method: string, tenantId: string): void {
  if (!tenantId) throw new Error(`TaskRepository.${method}: tenantId is required`);
}

function mapPriorityToDb(priority: string | undefined): string {
  if (priority === 'critical') return 'urgent';
  if (priority && (DB_VALID_PRIORITIES as readonly string[]).includes(priority)) {
    return priority;
  }
  return 'medium';
}

// ============================================
// REPOSITORY
// ============================================

export const TaskRepository = {
  /**
   * Create a task. v2 extension: accepts optional alertId to wire the
   * Alert→Task seam (tasks.alert_id FK from migration 20260422000006).
   *
   * SECURITY: tenantId is required for tenant isolation.
   */
  createTask: async (
    task: Task,
    tenantId: string,
    storeId?: string,
    alertId?: string,
  ): Promise<Task> => {
    if (!tenantId) {
      throw new Error('TaskRepository.createTask: tenantId is required');
    }

    const dbTask: Record<string, unknown> = {
      id: task.id,
      tenant_id: tenantId,
      title: task.title,
      description: task.description || null,
      status: toDbTaskStatus(task.status),
      priority: mapPriorityToDb(task.priority),
      due_date: task.dueDate || null,
      assignee_id: task.assigneeId || null,
    };

    if (storeId) dbTask.store_id = storeId;
    if (task.type) dbTask.type = task.type;
    if (task.targetRole) dbTask.target_role = task.targetRole;
    if (task.sourceRuleId) dbTask.source_rule_id = task.sourceRuleId;
    if (alertId) dbTask.alert_id = alertId;

    const { data, error } = await supabase
      .from('tasks')
      .insert(dbTask)
      .select()
      .single();

    if (error) {
      console.error('TaskRepository.createTask failed:', error);
      throw error;
    }
    return data as unknown as Task;
  },

  /**
   * Update a task's status (stored as-is; see TASK_STATUSES).
   */
  updateTaskStatus: async (id: string, tenantId: string, status: Task['status']): Promise<void> => {
    requireTenant('updateTaskStatus', tenantId);
    const { error } = await supabase
      .from('tasks')
      .update({
        status: toDbTaskStatus(status),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('TaskRepository.updateTaskStatus failed:', error);
      throw error;
    }
  },

  /**
   * Update the assignee of a task.
   */
  updateTaskAssignee: async (id: string, tenantId: string, assigneeId: string | null): Promise<void> => {
    requireTenant('updateTaskAssignee', tenantId);
    const { error } = await supabase
      .from('tasks')
      .update({ assignee_id: assigneeId, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('TaskRepository.updateTaskAssignee failed:', error);
      throw error;
    }
  },

  /**
   * Submit a task for manager review (pending_review state).
   */
  submitTaskForReview: async (id: string, tenantId: string, submittedBy: string): Promise<void> => {
    requireTenant('submitTaskForReview', tenantId);
    const { error } = await updateTaskWithOptionalColumns(
      id,
      tenantId,
      { status: 'pending_review', updated_at: new Date().toISOString() },
      { submitted_for_review_at: new Date().toISOString(), submitted_for_review_by: submittedBy },
    );

    if (error) {
      console.error('TaskRepository.submitTaskForReview failed:', error);
      throw error;
    }
  },

  /**
   * Approve a task (manager action). Moves task from pending_review to done.
   */
  approveTask: async (
    id: string,
    tenantId: string,
    reviewedBy: string,
    comment?: string,
  ): Promise<void> => {
    requireTenant('approveTask', tenantId);
    const { error } = await updateTaskWithOptionalColumns(
      id,
      tenantId,
      { status: 'done', updated_at: new Date().toISOString() },
      {
        review_info: {
          reviewed_by: reviewedBy,
          reviewed_at: new Date().toISOString(),
          review_comment: comment || null,
          rejection_reason: null,
        },
      },
    );

    if (error) {
      console.error('TaskRepository.approveTask failed:', error);
      throw error;
    }
  },

  /**
   * Reject a task (manager action). Moves task back to in_progress with feedback.
   */
  rejectTask: async (
    id: string,
    tenantId: string,
    reviewedBy: string,
    rejectionReason: string,
  ): Promise<void> => {
    requireTenant('rejectTask', tenantId);
    const { error } = await updateTaskWithOptionalColumns(
      id,
      tenantId,
      { status: 'in_progress', updated_at: new Date().toISOString() },
      {
        review_info: {
          reviewed_by: reviewedBy,
          reviewed_at: new Date().toISOString(),
          review_comment: null,
          rejection_reason: rejectionReason,
        },
      },
    );

    if (error) {
      console.error('TaskRepository.rejectTask failed:', error);
      throw error;
    }
  },
};
